import { useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { useNavigate, useParams } from 'react-router'

import { routes, taskRoute } from '@/app/routes'
import {
  useConfirmField,
  useDeleteDocument,
  useDocuments,
  useRoadmap,
  useTasks,
} from '@/shared/api/queries'
import type { Document, DocumentField } from '@/shared/api/queries'
import { formatFullDate, useDeadlineText } from '@/shared/format'
import { useLocalization, useTranslate } from '@/shared/i18n'
import { put as keepInVault } from '@/shared/vault'
import { useKeepLocally, useVaultEntry } from '@/shared/vault'
import {
  areaIcon,
  areaOf,
  carriesArc,
  documentIcon,
  documentTypeLabel,
  evaluationsOf,
  isArc,
  numberHint,
  numberLabel,
  tasksNeedingDocument,
  urgencyOf,
} from '@/shared/model'
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  KeyValue,
  Row,
  Screen,
  Skeleton,
  Stack,
  StateBlock,
  Text,
  useToast,
} from '@/shared/ui'

import { statusLabel } from './DocumentsPage'
import './flip.css'

function FieldCard({ documentId, field }: { documentId: string; field: DocumentField }) {
  const t = useTranslate()
  const confirm = useConfirmField()
  const toast = useToast()
  const [value, setValue] = useState(field.value)

  return (
    <li>
      <Card flat>
        <Stack direction="row" justify="between" gap="sm">
          <Text as="span" role="body-strong">
            {field.key}
          </Text>
          <Badge status="warning">{t('documents.field.unsure')}</Badge>
        </Stack>
        <Stack gap="sm">
          <Field label={t('documents.field.value')}>
            {(control) => (
              <input
                {...control}
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            )}
          </Field>
          <Button
            size="sm"
            disabled={confirm.isPending}
            onClick={() =>
              confirm.mutate(
                { documentId, key: field.key, value },
                { onSuccess: () => toast(t('toast.field'), 'success') },
              )
            }
          >
            {t('documents.field.confirm')}
          </Button>
        </Stack>
      </Card>
    </li>
  )
}

function Passport({ document }: { document: Document }) {
  const t = useTranslate()
  const { language } = useLocalization()
  const deadline = useDeadlineText()
  const toast = useToast()

  const kept = useVaultEntry(document.id)
  const [turned, setTurned] = useState(false)
  const [editing, setEditing] = useState(false)
  const [typed, setTyped] = useState('')
  const [own, setOwn] = useState<string>()

  const number = own ?? kept?.number
  const acceptable = (entered: string) => (carriesArc(document) ? isArc(entered) : entered !== '')

  const urgency =
    document.status === 'failed'
      ? 'critical'
      : urgencyOf({ deadline: document.expires_on, hard_deadline: true })

  const read = document.fields.filter((field) => !field.needs_confirmation || field.confirmed)

  function copy() {
    if (!number) {
      return
    }

    void navigator.clipboard
      .writeText(number)
      .then(() => toast(t('documents.number.copied'), 'success'))
      .catch(() => toast(t('documents.number.copy_failed'), 'warning'))
  }

  return (
    <div
      className={turned ? 'flip flip--turned' : 'flip'}
      {...(document.number
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: (event: ReactMouseEvent) => {
              if (!editing && !(event.target as Element).closest('button')) {
                setTurned(!turned)
              }
            },
            onKeyDown: (event: ReactKeyboardEvent) => {
              if (!editing && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                setTurned(!turned)
              }
            },
          }
        : {})}
    >
      <div className="flip__inner">
        <div className="flip__face">
          <Card inverse className={`tone--${urgency}`}>
            <Stack direction="row" justify="between" align="start" gap="sm">
              <Text as="span" role="title">
                {documentIcon(document.type)}
              </Text>
              <Badge tone>
                {urgency === 'critical' || urgency === 'attention'
                  ? (deadline(document.expires_on) ?? t(statusLabel(document.status)))
                  : t(statusLabel(document.status))}
              </Badge>
            </Stack>

            <Text as="h1" role="heading-2">
              {t(documentTypeLabel(document.type))}
            </Text>

            <div className="grid-2">
              {document.number ? (
                <KeyValue label={t(numberLabel(document))} mono>
                  {document.number}
                </KeyValue>
              ) : null}
              {document.issuing_country ? (
                <KeyValue label={t('documents.country')}>{document.issuing_country}</KeyValue>
              ) : null}
              {document.issued_on ? (
                <KeyValue label={t('documents.issue')}>
                  {formatFullDate(document.issued_on, language)}
                </KeyValue>
              ) : null}
              {document.expires_on ? (
                <KeyValue label={t('documents.expiry')}>
                  {formatFullDate(document.expires_on, language)}
                </KeyValue>
              ) : null}
              {read.map((field) => (
                <KeyValue key={field.key} label={field.key}>
                  {field.value}
                </KeyValue>
              ))}
            </div>

            {document.number ? (
              <Text role="caption" tone="on-inverse">
                {t('documents.number.tap')}
              </Text>
            ) : null}
          </Card>
        </div>

        <div className="flip__face flip__face--back" aria-hidden={!turned}>
          <Card inverse className={`tone--${urgency}`}>
            <Text role="eyebrow" tone="on-inverse">
              {t(numberLabel(document))}
            </Text>

            <div className="flip__number">
              {editing ? (
                <Stack gap="sm" className="flip__form">
                  <input
                    className="flip__input"
                    value={typed}
                    inputMode="text"
                    autoFocus
                    onChange={(event) => setTyped(event.target.value.toUpperCase())}
                  />
                  <Text role="caption" tone="on-inverse" measure>
                    {t(numberHint(document))}
                  </Text>
                  <Stack direction="row" gap="sm">
                    <Button
                      size="sm"
                      disabled={!acceptable(typed.trim())}
                      onClick={() => {
                        const entered = typed.trim()

                        if (acceptable(entered)) {
                          setOwn(entered)
                          void keepInVault({ documentId: document.id, number: entered })
                          toast(t('documents.number.saved'), 'success')
                        }

                        setEditing(false)
                      }}
                    >
                      {t('documents.number.save')}
                    </Button>
                    <Button size="sm" variant="quiet" onClick={() => setEditing(false)}>
                      {t('app.cancel')}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <>
                  <Text as="p" role="heading-1" className="flip__digits">
                    {number ?? document.number}
                  </Text>

                  <Stack direction="row" gap="sm" className="flip__copy">
                    {number ? (
                      <Button size="sm" variant="quiet" onClick={copy}>
                        {t('documents.number.copy')}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="quiet"
                      onClick={() => {
                        setTyped(number ?? '')
                        setEditing(true)
                      }}
                    >
                      {t('documents.number.edit')}
                    </Button>
                  </Stack>

                  {number ? null : (
                    <Text role="caption" tone="on-inverse" measure>
                      {t('documents.number.not_kept')}
                    </Text>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

function Details({ document }: { document: Document }) {
  const t = useTranslate()
  const navigate = useNavigate()
  const deadline = useDeadlineText()

  const remove = useDeleteDocument()
  const { forget } = useKeepLocally()
  const toast = useToast()
  const tasks = useTasks()
  const roadmap = useRoadmap()
  const [confirming, setConfirming] = useState(false)

  const evaluations = evaluationsOf(roadmap.data)
  const unlocks = tasksNeedingDocument(document.type, tasks.data ?? [], evaluations).filter(
    (task) => task.status === 'open',
  )

  const unsure = document.fields.filter((field) => field.needs_confirmation && !field.confirmed)

  return (
    <Stack gap="md" className="page--detail">
      <Passport document={document} />

      {document.failure_reason ? (
        <Alert
          status="danger"
          title={t('documents.analysis.failed', { reason: document.failure_reason })}
        />
      ) : null}

      {unlocks.length > 0 ? (
        <Card>
          <Stack direction="row" gap="sm" className="tone--attention">
            <span className="dot" aria-hidden="true" />
            <Text as="h2" role="eyebrow" tone="muted">
              {t('documents.unlocks.title')}
            </Text>
          </Stack>
          <Stack as="ul" gap="xs">
            {unlocks.map((task, index) => (
              <Row
                key={task.id}
                variant="plain"
                to={taskRoute(task.id)}
                className={`tone--${urgencyOf(task)}`}
                icon={String(index + 1)}
                title={task.title}
                meta={`${areaIcon(areaOf(task, evaluations) ?? 'other')} ${deadline(task.deadline) ?? t('tasks.no_deadline')}`}
              />
            ))}
          </Stack>
        </Card>
      ) : null}

      {unsure.length > 0 ? (
        <Stack gap="sm">
          <Text as="h2" role="eyebrow" tone="muted">
            {t('documents.fields')}
          </Text>
          <Stack as="ul" gap="sm">
            {unsure.map((field) => (
              <FieldCard key={field.key} documentId={document.id} field={field} />
            ))}
          </Stack>
        </Stack>
      ) : null}

      {confirming ? (
        <Stack gap="sm">
          <Text role="caption" tone="muted" measure>
            {t('documents.delete.confirm')}
          </Text>
          <Stack direction="row" gap="sm">
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(document.id, {
                  onSuccess: () => {
                    // The device keeps its own copy, and a deleted document
                    // must not leave one behind.
                    forget(document.id)
                    toast(t('toast.document.deleted'))
                    void navigate(routes.documents)
                  },
                })
              }
            >
              {t('documents.delete')}
            </Button>
            <Button variant="quiet" onClick={() => setConfirming(false)}>
              {t('app.cancel')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" gap="sm">
          <Button variant="quiet" size="sm" onClick={() => setConfirming(true)}>
            {t('documents.delete')}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export function DocumentPage() {
  const t = useTranslate()
  const { id } = useParams()
  const documents = useDocuments()

  const document = documents.data?.find((candidate) => candidate.id === id)

  if (documents.data && !document) {
    return <Screen title={t('error.generic.title')} />
  }

  return (
    <StateBlock
      pending={documents.isPending}
      error={documents.error}
      onRetry={() => void documents.refetch()}
      skeleton={<Skeleton variant="card" rows={3} title />}
    >
      {document ? <Details document={document} /> : null}
    </StateBlock>
  )
}
